'use client';

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AnimatedButton } from './AnimatedButton';
import { RESERVATION_URL, GOOGLE_BUSINESS_URL } from '../lib/site';

interface Review {
  id: number;
  author: string;
  role: string;
  content: string;
  rating: number;
  source: 'Planity' | 'Google';
  date: string;
}

const reviews: Review[] = [
  {
    id: 1,
    author: 'Client Planity',
    role: 'Avis vérifié · 22/01/2026',
    content: 'Foncez c léger fami (',
    rating: 5,
    source: 'Planity',
    date: '22/01/2026',
  },
  {
    id: 2,
    author: 'Client Planity',
    role: 'Avis vérifié · 14/01/2026',
    content: 'Très bonne expérience',
    rating: 5,
    source: 'Planity',
    date: '14/01/2026',
  },
  {
    id: 3,
    author: 'Client Planity',
    role: 'Avis vérifié · 24/12/2025',
    content: 'Parfait je conseil fortement des coiffeurs au top',
    rating: 5,
    source: 'Planity',
    date: '24/12/2025',
  },
  {
    id: 4,
    author: 'Ismael ABDOU',
    role: 'Avis vérifié · 03/10/2025',
    content:
      "Première coupe pour notre bébé et nous sommes ravis ! L'équipe a été très douce, patiente et attentionnée. Le coiffeur Sharko a su mettre notre petit à l'aise dès le début, malgré son agitation. Le résultat est parfait et le moment s'est très bien passé. Pour ma part, le coiffeur sharko a su répondre à mes attentes. Le résultat de ma coupe était parfait aussi. Merci encore pour votre professionnalisme et votre bienveillance ! Nous reviendrons sans hésiter.",
    rating: 5,
    source: 'Google',
    date: '03/10/2025',
  },
  {
    id: 5,
    author: 'Zxoric',
    role: 'Avis vérifié · 27/09/2025',
    content:
      "J'ai été couper par Kevin super coiffeur, accueillant, il est à l'écoute, et prend le temps de soigner la coupe du client, il est très à l'aise et n'hésite pas à parler avec son client",
    rating: 5,
    source: 'Google',
    date: '27/09/2025',
  },
  {
    id: 6,
    author: 'Brigitte Lebaud',
    role: 'Avis vérifié · 15/09/2025',
    content:
      'Je suis coiffeuse, mais retraitée, donc mes petits fils vont se faire coiffer à Eleganz Barber. Super coupe, très professionnel, très stylé comme ils disent. Enfin des coiffeurs qui savent bien utiliser une tondeuse et font des beaux dégradés sans échelle.',
    rating: 5,
    source: 'Google',
    date: '15/09/2025',
  },
];

const Star = ({ filled = true }: { filled?: boolean }) => (
  <svg
    viewBox="0 0 24 24"
    className={`w-4 h-4 ${filled ? 'fill-yellow-400 text-yellow-400' : 'fill-gray-200 text-gray-200'}`}
  >
    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
  </svg>
);

const ChevronDown = ({ className = '' }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <path d="m6 9 6 6 6-6" />
  </svg>
);

function ReviewCard({ review }: { review: Review }) {
  return (
    <div className="flex flex-col h-full bg-white p-6 sm:p-7 rounded-3xl border border-gray-100 group transition-all duration-300 hover:shadow-xl hover:shadow-gray-100/50 hover:-translate-y-1">
      <div className="flex items-center justify-between gap-3 mb-5">
        <div className="flex gap-1">
          {[...Array(5)].map((_, i) => (
            <Star key={i} filled={i < review.rating} />
          ))}
        </div>
        <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
          {review.source}
        </span>
      </div>

      <p className="flex-grow text-gray-600 font-sans text-sm/relaxed mb-6">
        «&nbsp;{review.content}&nbsp;»
      </p>

      <div className="mt-auto pt-4 border-t border-gray-100">
        <p className="font-sans font-semibold text-[#1d1d1d] text-sm">{review.author}</p>
        <p className="text-gray-400 font-sans text-[11px] tracking-wide uppercase mt-0.5">
          {review.role}
        </p>
      </div>
    </div>
  );
}

interface FAQ {
  question: string;
  answer: string;
}

const faqs: FAQ[] = [
  {
    question: 'Comment être sûr que la coupe me correspondra ?',
    answer:
      "Chaque rendez-vous chez Eleganze commence par un diagnostic personnalisé. Notre barbier analyse la forme de votre visage, votre type de cheveux, votre style de vie et votre routine quotidienne avant de proposer une coupe adaptée. L'objectif est double : un résultat qui vous met en valeur immédiatement, et une coupe facile à entretenir au quotidien sans effort particulier. Vous repartez avec des conseils concrets sur les produits et le coiffage. C'est cette approche sur mesure, systématique à chaque visite, qui fait revenir nos clients régulièrement.",
  },
  {
    question: 'Pourquoi choisir Eleganze comme barber à Poitiers ?',
    answer:
      "Eleganze est un salon barbier situé à Buxerolles, à 5 minutes du centre de Poitiers, spécialisé dans la coupe homme, le dégradé et la taille de barbe. Le salon se distingue par une constance dans la qualité des finitions, un accueil attentionné à chaque passage et une réservation en ligne rapide via Planity. Avec une note de 4,9/5 sur Planity, Eleganze est l'un des barbiers les mieux notés de l'agglomération de Poitiers. L'équipe reçoit du mardi au samedi de 10h à 20h, sans interruption.",
  },
  {
    question: 'Quels services sont les plus demandés chez Eleganze ?',
    answer:
      "Les prestations les plus demandées sont la coupe homme dès 25 €, la formule coupe + barbe à 35 €, et le dégradé américain à 20 €. Le contour simple (15 €) et la barbe tondeuse (15 €) sont également très populaires pour un entretien rapide entre deux coupes. Pour les plus jeunes, une coupe enfant est disponible à 17 €. La durée moyenne d'une prestation est de 30 minutes. Toutes les prestations peuvent être réservées en ligne sur Planity ou en walk-in selon les disponibilités du jour.",
  },
  {
    question: "Comment accéder au salon depuis Poitiers centre ?",
    answer:
      "Eleganze Barber est situé au 26 rue de Terrageaux, 86180 Buxerolles, à 5 minutes en voiture du centre de Poitiers via la RD910. Le salon est facilement accessible depuis les quartiers nord de Poitiers, Buxerolles, Migné-Auxances et les communes voisines. Un parking est disponible à proximité. Le salon est ouvert du mardi au samedi de 10h à 20h. La réservation en ligne est recommandée mais des places walk-in sont disponibles selon la disponibilité du jour.",
  },
  {
    question: 'Combien de temps dure une prestation chez Eleganze ?',
    answer:
      "La grande majorité des prestations barbier chez Eleganze dure 30 minutes : coupe homme, dégradé, barbe tondeuse, contour ou coupe + barbe. Les prestations techniques comme le taper fade, le mulet ou la transformation cheveux longs vers courts peuvent prendre jusqu'à 45 minutes. Le soin visage complet (gommage, vapeur, masque) dure 35 minutes. Ces durées incluent le diagnostic initial et les finitions. Il est recommandé de réserver son créneau en ligne pour garantir sa disponibilité.",
  },
  {
    question: 'Eleganze propose-t-il également des services pour les femmes et les enfants ?',
    answer:
      "Eleganze est avant tout un salon barbier spécialisé dans les soins homme : coupe, barbe et dégradé. Des coupes enfant sont proposées à partir de 17 € pour les moins de 13 ans. Le salon dispose également d'un espace onglerie accueillant toute la clientèle pour des poses gel, nail art, soins des ongles et beauté du regard, ouvert aux femmes comme aux hommes. L'équipe compte aussi une spécialiste en épilation et soins du visage disponible sur rendez-vous.",
  },
];

function FAQItem({ faq, isOpen, onClick }: { faq: FAQ; isOpen: boolean; onClick: () => void }) {
  return (
    <div className="border-b border-gray-200/60 last:border-0">
      <button
        onClick={onClick}
        className="w-full py-5 sm:py-6 flex items-center justify-between gap-4 text-left focus:outline-none group"
      >
        <span className="font-sans font-medium text-[#1d1d1d] pr-6 sm:pr-8 group-hover:text-gray-600 transition-colors text-sm sm:text-base">
          {faq.question}
        </span>
        <ChevronDown
          className={`w-5 h-5 text-gray-400 transition-transform duration-300 shrink-0 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="pb-5 sm:pb-6 pr-8 sm:pr-12 text-sm/relaxed text-gray-500 font-sans font-light">
              {faq.answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export const TestimonialsAndFAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section id="avis" className="w-full bg-white">
      {/* Reviews */}
      <div className="py-20 sm:py-24 md:py-28 border-b border-gray-100">
        <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-12 sm:mb-16 lg:mb-20">
            <p className="text-gray-400 font-sans text-[11px] sm:text-xs tracking-[0.2em] uppercase mb-3 sm:mb-4 font-medium">
              Ce que disent nos clients
            </p>
            <h2 className="text-4xl md:text-6xl lg:text-7xl font-black uppercase leading-[0.95] tracking-tight text-[#1d1d1d] mb-4 max-w-5xl">
              LA SATISFACTION CLIENT
            </h2>
            <p className="text-gray-500 font-sans text-sm sm:text-base max-w-xl mx-auto font-light">
              Découvrez les retours authentiques de ceux qui nous ont fait confiance. Une expérience premium, racontée par nos clients.
            </p>
            <div className="mt-7 flex justify-center">
              <div className="h-px w-16 bg-gray-300" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 lg:gap-8">
            {reviews.map((review) => (
              <div key={review.id} className="h-full">
                <ReviewCard review={review} />
              </div>
            ))}
          </div>

          <div className="mt-10 sm:mt-12 flex justify-center">
            <AnimatedButton
              href={GOOGLE_BUSINESS_URL}
              target="_blank"
              rel="noopener noreferrer"
              dark
            >
              Donner mon avis sur Google
            </AnimatedButton>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div id="faq" className="py-20 sm:py-24 md:py-32 bg-white">
        <div className="mx-auto max-w-3xl px-5 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16 lg:mb-20">
            <p className="text-gray-400 font-sans text-[11px] sm:text-xs tracking-[0.2em] uppercase mb-3 sm:mb-4 font-medium">
              Questions fréquentes
            </p>
            <h2 className="text-4xl md:text-6xl lg:text-7xl font-black uppercase leading-[0.95] tracking-tight text-[#1d1d1d] mb-6 sm:mb-8 max-w-5xl">
              TOUT CE QUE VOUS DEVEZ SAVOIR SUR ELEGANZE
            </h2>

            <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4">
              <AnimatedButton
                href={RESERVATION_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto justify-center"
              >
                Réserver
              </AnimatedButton>
              <a
                href="#soins-coiffure"
                className="w-full sm:w-auto px-7 sm:px-8 py-3 sm:py-3.5 rounded-full bg-transparent text-[#1d1d1d] border border-gray-200 font-sans text-xs tracking-wider uppercase hover:bg-gray-50 transition-colors text-center"
              >
                Voir les prestations
              </a>
            </div>
          </div>

          <div className="border-t border-gray-200/60">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                faq={faq}
                isOpen={openIndex === index}
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
